import React from "react";
import { NAVIGATION_ITEMS, NavigationItem, hasAccess } from "../../../../../config/routes";
import { WidgetComponentProps } from "../../types";
import { ListRows } from "../../ui";

const MAX_LINKS = 3;

/** Depth-first lookup of a nav item by its path. */
const findNavItem = (items: NavigationItem[], path: string): NavigationItem | undefined => {
  for (const item of items) {
    if (item.path === path) return item;
    const child = item.children ? findNavItem(item.children, path) : undefined;
    if (child) return child;
  }
  return undefined;
};

interface LinkTileProps extends WidgetComponentProps {
  /** Path of the tool's nav item; its children become the sub-links. */
  navPath: string;
}

/**
 * Stat-less tile: up to three sub-pages of the tool. Tools without sub-pages
 * render no body, leaving just the header and the card's "Open" footer.
 */
export const LinkTileCard: React.FC<LinkTileProps> = ({ access, navPath }) => {
  const item = findNavItem(NAVIGATION_ITEMS, navPath);
  const links = (item?.children ?? []).filter((c) => hasAccess(c, access)).slice(0, MAX_LINKS);
  if (links.length === 0) return null;

  return (
    <ListRows
      rows={links.map((l) => ({
        key: l.path,
        primary: (
          <span className="home-card__link">
            <i className={l.icon} /> {l.label}
          </span>
        ),
        to: l.path,
      }))}
    />
  );
};

/** Bind a nav path into a component usable as `WidgetDefinition.Component`. */
export const makeLinkTile = (navPath: string): React.FC<WidgetComponentProps> => {
  const Tile: React.FC<WidgetComponentProps> = (props) => <LinkTileCard {...props} navPath={navPath} />;
  Tile.displayName = `LinkTile(${navPath})`;
  return Tile;
};

export default LinkTileCard;
